const TIME_ZONE_KEYS = [
  'timeZone',
  'timezone',
  'displayTimeZone',
  'displayTimezone',
  'siteTimeZone',
  'siteTimezone',
  'deviceTimeZone',
  'deviceTimezone',
  'locationTimeZone',
  'locationTimezone',
  'ianaTimeZone',
  'ianaTimezone',
] as const;

const RAW_TEMPORAL_PRIORITY_KEYS = [
  'timestamp',
  'dateTime',
  'datetime',
  'recordedAt',
  'recorded_at',
  'sampledAt',
  'sampled_at',
  'measuredAt',
  'measured_at',
  'observedAt',
  'observed_at',
  'eventTime',
  'event_time',
  'createdAt',
  'updatedAt',
] as const;

const TEMPORAL_FIELD_NAME_PATTERN = /(time|date|timestamp|datetime|recorded|sampled|measured|observed)/i;

type TimeZoneCandidate = string | null | undefined;

export const isValidTimeZone = (timeZone: TimeZoneCandidate): timeZone is string => {
  if (!timeZone || typeof timeZone !== 'string') {
    return false;
  }

  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
};

export const getBrowserTimeZone = (): string | undefined => {
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return isValidTimeZone(browserTimeZone) ? browserTimeZone : undefined;
};

export const extractTimeZoneFromRecord = (
  record?: Record<string, unknown> | null
): string | undefined => {
  if (!record) {
    return undefined;
  }

  for (const key of TIME_ZONE_KEYS) {
    const candidate = record[key];
    if (typeof candidate === 'string' && isValidTimeZone(candidate)) {
      return candidate;
    }
  }

  return undefined;
};

export const resolvePreferredTimeZone = (
  ...candidates: TimeZoneCandidate[]
): string | undefined => {
  for (const candidate of candidates) {
    if (isValidTimeZone(candidate)) {
      return candidate;
    }
  }

  return undefined;
};

const isIsoLikeTemporalString = (value: string): boolean => (
  /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/.test(value.trim())
);

export const isStructuredTemporalValue = (value: unknown): boolean => {
  if (value instanceof Date) {
    return Number.isFinite(value.getTime());
  }

  if (typeof value === 'number') {
    return value > 946684800000 && value < 4102444800000;
  }

  if (typeof value === 'string') {
    return isIsoLikeTemporalString(value);
  }

  return false;
};

export const isTemporalFieldName = (fieldName?: string | null): boolean => {
  if (!fieldName) {
    return false;
  }

  return TEMPORAL_FIELD_NAME_PATTERN.test(fieldName);
};

export const findRawTemporalField = (
  record?: Record<string, unknown> | null,
  preferredField?: string
): string | undefined => {
  if (!record) {
    return undefined;
  }

  if (preferredField && isStructuredTemporalValue(record[preferredField])) {
    return preferredField;
  }

  const preferredFieldLower = preferredField?.toLowerCase();

  const directCandidates = RAW_TEMPORAL_PRIORITY_KEYS.filter((key) => key in record);
  for (const key of directCandidates) {
    if (key !== preferredField && isStructuredTemporalValue(record[key])) {
      return key;
    }
  }

  if (preferredFieldLower) {
    const suffixCandidates = Object.keys(record).filter((key) => {
      const keyLower = key.toLowerCase();
      return key !== preferredField && (
        keyLower === `${preferredFieldLower}timestamp` ||
        keyLower === `${preferredFieldLower}_timestamp` ||
        keyLower === `${preferredFieldLower}datetime` ||
        keyLower === `${preferredFieldLower}_datetime` ||
        keyLower.endsWith('timestamp') ||
        keyLower.endsWith('datetime')
      );
    });

    for (const key of suffixCandidates) {
      if (isStructuredTemporalValue(record[key])) {
        return key;
      }
    }
  }

  return undefined;
};
